import { useState, useEffect, useCallback } from 'react';
import { holidayMasterApi, DATE_PRESET_OPTIONS, STATUS_OPTIONS } from '../api/holidayMasterApi';
import { appToast } from '../../../../components/AppToast';
import { appConfirm } from '../../../../components/AppConfirmModal';
import { useExportJob } from '../../../../hooks/useExportJob';

export function useHolidayMasterList() {
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [availableGroups, setAvailableGroups] = useState([]);

  // Filter states
  const [searchField, setSearchField] = useState('Name');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('Active'); // Default to Active as required
  const [empGroupFilter, setEmpGroupFilter] = useState('All');
  const [datePresetFilter, setDatePresetFilter] = useState('Current Financial Year');
  const [fromDate, setFromDate] = useState('2026-04-01');
  const [toDate, setToDate] = useState('2027-03-31');

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Load available employee groups
  useEffect(() => {
    holidayMasterApi.getEmployeeGroups().then((groups) => {
      setAvailableGroups(groups);
    });
  }, []);

  const fetchRecords = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await holidayMasterApi.getAll({
        name: searchText,
        group: empGroupFilter,
        status: statusFilter,
        fromDate,
        toDate,
        page,
        pageSize,
      });
      setRecords(res.items || []);
      setTotalCount(res.total_count || 0);
      setTotalPages(res.total_pages || 1);
    } catch (err) {
      console.error(err);
      appToast.error('Failed to fetch holiday records.');
    } finally {
      setIsLoading(false);
    }
  }, [searchText, empGroupFilter, statusFilter, fromDate, toDate, page, pageSize]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const handleDatePresetChange = (preset) => {
    setDatePresetFilter(preset);
    if (preset === 'Current Financial Year' || preset === 'This Financial Year') {
      setFromDate('2026-04-01');
      setToDate('2027-03-31');
    } else if (preset === 'This Month') {
      setFromDate('2026-08-01');
      setToDate('2026-08-31');
    } else if (preset === 'Last Month') {
      setFromDate('2026-07-01');
      setToDate('2026-07-31');
    } else if (preset === 'Today') {
      setFromDate('2026-08-31');
      setToDate('2026-08-31');
    } else if (preset === 'All') {
      setFromDate('');
      setToDate('');
    }
    setPage(1);
  };

  const handleSearch = () => {
    setPage(1);
    fetchRecords();
  };

  const resetFilters = () => {
    setSearchField('Name');
    setSearchText('');
    setStatusFilter('Active');
    setEmpGroupFilter('All');
    setDatePresetFilter('All');
    setFromDate('');
    setToDate('');
    setPage(1);
  };

  const handleArchive = async (item) => {
    const isCurrentlyActive = item.status === 'Active';
    const actionText = isCurrentlyActive ? 'Archive' : 'Activate';

    const confirmed = await appConfirm({
      title: `${actionText} Holiday`,
      message: `Are you sure you want to ${actionText.toLowerCase()} holiday "${item.name}"?`,
      confirmText: actionText,
      confirmVariant: isCurrentlyActive ? 'warning' : 'primary',
    });

    if (!confirmed) return;

    try {
      const res = await holidayMasterApi.archive(item.id);
      appToast.success(res.message || `Holiday ${actionText.toLowerCase()}d successfully.`);
      fetchRecords();
    } catch (err) {
      console.error(err);
      appToast.error(err?.response?.data?.detail || `Failed to ${actionText.toLowerCase()} holiday.`);
    }
  };

  const handleDelete = async (id, name) => {
    const confirmed = await appConfirm({
      title: 'Delete Holiday',
      message: `Are you sure you want to delete holiday "${name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      confirmVariant: 'danger',
    });

    if (!confirmed) return;

    try {
      await holidayMasterApi.delete(id);
      appToast.success(`Holiday "${name}" deleted successfully.`);
      fetchRecords();
    } catch (err) {
      console.error(err);
      appToast.error(err?.response?.data?.detail || 'Failed to delete holiday.');
    }
  };

  const { triggerExport, isExporting } = useExportJob({
    module: 'holiday_master',
    fileFormat: 'XLSX',
  });

  const handleToolSelect = (toolKey) => {
    if (toolKey === 'download' || toolKey === 'export_excel') {
      triggerExport({
        search: searchText,
        emp_group: empGroupFilter,
        status_filter: statusFilter,
        from_date: fromDate,
        to_date: toDate,
      });
    } else if (toolKey === 'print') {
      window.print();
    } else {
      appToast.info(`Selected tool: ${toolKey}`);
    }
  };

  const searchFieldOptions = [
    { value: 'Name', label: 'Name' },
  ];

  const empGroupOptions = [
    { value: 'All', label: 'All Groups' },
    ...availableGroups.map((g) => ({
      value: typeof g === 'object' && g !== null ? String(g.id) : String(g),
      label: typeof g === 'object' && g !== null ? g.name : String(g),
    })),
  ];

  const tools = [
    { key: 'download', label: 'Download', icon: 'file_download' },
    { key: 'print', label: 'Print', icon: 'print' },
  ];

  return {
    state: {
      records,
      isLoading,
      isExporting,
      searchField,
      searchText,
      statusFilter,
      empGroupFilter,
      datePresetFilter,
      fromDate,
      toDate,
      page,
      pageSize,
      totalPages,
      totalCount,
      searchFieldOptions,
      empGroupOptions,
      datePresetOptions: DATE_PRESET_OPTIONS,
      statusOptions: STATUS_OPTIONS,
      tools,
    },
    actions: {
      setSearchField,
      setSearchText,
      setStatusFilter,
      setEmpGroupFilter,
      setDatePresetFilter: handleDatePresetChange,
      setFromDate,
      setToDate,
      setPage,
      setPageSize,
      handleSearch,
      resetFilters,
      handleArchive,
      handleDelete,
      handleToolSelect,
      refresh: fetchRecords,
    },
  };
}
